import React, { useState, useEffect } from "react";
import { Box, Button, TextField, TablePagination, Tooltip } from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { CopyToClipboard } from "react-copy-to-clipboard";
import moment from "moment-timezone";

import { endPoints } from "../../../constant/Environment";
import { addData } from "../../../Utility/API";
import { getSessonId } from "../../../Utility/axiosInstances";
import PageHeader from "../../../components/PageHeader";
import SectionCard from "../../../components/SectionCard";
import Loader from "../../../components/Loader";
import { tokens } from "../../../theme";
import { mono, th, td, TablePaginationActions } from "../../../components/tableKit";

const fmtDateTime = (d) =>
  d && moment.utc(d).isValid() ? moment.utc(d).format("YYYY-MM-DD HH:mm:ss") : "";

const csvBtnSx = { textTransform: "none", fontSize: 12, color: tokens.textDim, borderColor: tokens.border, "&:hover": { borderColor: tokens.border2 } };

const AccessList = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    let url = `${endPoints.api.GET_Access_List}`;
    let data = {
      userId: 0,
      agencyId: 0,
      operatorId: 0,
      showRiskmanager: true,
      logoutType: 0,
      sessionCode: "string",
      sessionId: 0,
      pagination: {
        pageSize: 0,
        pageNumber: 0,
        maxLimit: 100,
      },
      fromDate: "2022-06-10T16:31:57.262Z",
      toDate: "2022-06-10T16:31:57.262Z",
    };
    await addData(url, data)
      .then((response) => {
        setIsLoading(false);
        if (response?.status == 200) {
          setRows(response.data.data);
        }
      })
      .catch((error) => {
        setIsLoading(false);
        console.error(error);
      });
  };

  const exportReport = (format) => {
    const body = { userId: 0, startdate: "", enddate: "" };
    // Raw fetch, so it misses the axios interceptor that attaches SessonId —
    // set it by hand or the API answers 401 and we save the error body as the
    // report. Accept admits the octet-stream the download endpoints produce.
    const sessonId = getSessonId();
    fetch(`${endPoints.apiBaseUrl + endPoints.api.ACCESS_REPORT + "/" + format}`, {
      method: "POST",
      body: JSON.stringify(body),
      headers: {
        "Content-Type": "application/json",
        Accept: "*/*",
        ...(sessonId ? { SessonId: sessonId } : {}),
      },
    }).then(function (response) {
      response.blob().then((blob) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `AccessListReport.${format}`;
        link.click();
      });
    });
  };

  const filtered = filterText
    ? rows.filter((item) => {
        const q = filterText.toLowerCase();
        return (
          String(item.login || "").toLowerCase().includes(q) ||
          String(item.idUser || "").includes(q) ||
          String(item.ipAddress || "").includes(q) ||
          String(item.sessionCode || "").toLowerCase().includes(q)
        );
      })
    : rows;

  return (
    <Box>
      {isLoading && <Loader spinner={true} visible={isLoading} />}

      <PageHeader
        title="Access List"
        crumb="Users / Access List"
        subtitle="Player sessions · devices, IPs & balances"
      />

      <SectionCard>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center", mb: 2 }}>
          <TextField
            size="small"
            placeholder="Filter by player, session, ID or IP"
            value={filterText}
            onChange={(e) => {
              setFilterText(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 260, "& .MuiInputBase-root": { fontSize: 13 } }}
          />
          <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
            <Button size="small" variant="outlined" startIcon={<FileDownloadOutlinedIcon />} sx={csvBtnSx} onClick={() => exportReport("pdf")}>
              PDF
            </Button>
            <Button size="small" variant="outlined" startIcon={<FileDownloadOutlinedIcon />} sx={csvBtnSx} onClick={() => exportReport("csv")}>
              CSV
            </Button>
          </Box>
        </Box>

        <Box sx={{ overflowX: "auto" }}>
          <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={th}>Session</th>
                <th style={th}>Player</th>
                <th style={th}>Start</th>
                <th style={th}>End</th>
                <th style={th}>Device</th>
                <th style={th}>Browser / Platform</th>
                <th style={th}>IP Address</th>
                <th style={th}>Initial Balance</th>
                <th style={th}>Final Balance</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && !isLoading && (
                <tr>
                  <td colSpan={9} style={{ ...td, textAlign: "center", color: tokens.textDim }}>
                    No sessions found
                  </td>
                </tr>
              )}
              {filtered
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, i) => (
                  <tr key={`${row.sessionCode}-${i}`}>
                    <td style={td}>
                      <div style={{ fontFamily: mono }}>#{row.idUser}</div>
                      <div style={{ fontFamily: mono, fontSize: 10.5, color: tokens.textDim2 }}>{row.sessionCode}</div>
                    </td>
                    <td style={{ ...td, fontWeight: 600 }}>{row.login}</td>
                    <td style={{ ...td, fontFamily: mono, color: tokens.textDim }}>{fmtDateTime(row.startTime)}</td>
                    <td style={{ ...td, fontFamily: mono, color: tokens.textDim }}>{fmtDateTime(row.endTime)}</td>
                    <td style={td}>
                      <div>{row.device}</div>
                      {row.deviceId && (
                        <Tooltip title="Click to copy device ID">
                          <span>
                            <CopyToClipboard text={row.deviceId}>
                              <span style={{ fontFamily: mono, fontSize: 10.5, color: tokens.textDim2, cursor: "pointer" }}>
                                {String(row.deviceId).slice(0, 18)}
                                {String(row.deviceId).length > 18 ? "…" : ""}
                              </span>
                            </CopyToClipboard>
                          </span>
                        </Tooltip>
                      )}
                    </td>
                    <td style={td}>
                      <div>{row.browser}</div>
                      <div style={{ fontSize: 10.5, color: tokens.textDim2 }}>{row.operatingSystem}</div>
                    </td>
                    <td style={{ ...td, fontFamily: mono }}>{row.ipAddress}</td>
                    <td style={{ ...td, fontFamily: mono }}>{row.balanceStart}</td>
                    <td style={{ ...td, fontFamily: mono, fontWeight: 700 }}>{row.balanceEnd}</td>
                  </tr>
                ))}
            </tbody>
          </Box>
        </Box>

        <TablePagination
          component="div"
          rowsPerPageOptions={[10, 25, 50, 100]}
          count={filtered.length}
          rowsPerPage={rowsPerPage}
          page={page}
          SelectProps={{ inputProps: { "aria-label": "rows per page" }, native: true }}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          ActionsComponent={TablePaginationActions}
          sx={{ borderTop: `1px solid ${tokens.border}`, mt: 1 }}
        />
      </SectionCard>
    </Box>
  );
};

export default AccessList;
